import { Form, message } from 'antd'
import { throttle } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useLingui } from '@lingui/react/macro'

import { useGeneratePolishPromp } from '../../_hooks/useGeneratePolishPromp'
import { useInitGeneratorType } from '../../_hooks/initGeneratorType'

import { useJoinCreditsHintVisibility } from './_hooks/useJoinCreditsHintVisibility'
import { GeneratorBtnActionArea } from './_comp/generatorBtnActionArea'
import { LeftImageUpload } from './_comp/leftImageUpload'
import { TopContent } from './_comp/topContent'

import type { UploadImageRef } from './_comp/uploadImage'
import type { PromptMode } from '@loc/server/interface/task'
import type { FormInstance } from 'antd/lib'
import type { MusicGeneratorFormValuesNew } from '@/pages/tad.ai/ai-music-generator/_types/form'

import { useMusicFormStore } from '@/pages/tad.ai/ai-music-generator/_store/useMusicFormStore'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { ImageDisplayStatus } from '@/components/ImageDisplay/type'
import EnhanceTextArea from '@/components/EnhanceTextArea'
import { EFeatureName, useFeatureGuard } from '@/hooks/usageValidation'
import { useIsMounted } from '@/hooks/isMounted'
import {
  GenerateLyricsSmartType,
  useAiMusicGeneratorStoreFromZustand,
} from '@/pages/tad.ai/ai-music-generator/_store/createStore'
import { useAppUIModal } from '@/contexts/AppUIModalContext'
import { AI_MUSIC_GENERATOR_MAX_PROMPT_LENGTH } from '@/pages/tad.ai/ai-music-generator/const'

export interface IGeneratorLyricsSmart {
  form: FormInstance<MusicGeneratorFormValuesNew>
  outwardEvent?: () => void
  textareaStyle?: React.CSSProperties
  isEntryPage?: boolean
}

const GeneratorLyricsSmart = (props: IGeneratorLyricsSmart) => {
  const { form, outwardEvent, textareaStyle, isEntryPage = false } = props
  const { t } = useLingui()
  const taskType = Form.useWatch('taskType', form)
  const thinking = Form.useWatch('thinking', form)
  const imageUrl = Form.useWatch('imageUrl', form)
  const prompt = Form.useWatch('prompt', form)
  const promptSelectMode = Form.useWatch('promptSelectMode', form)
  const {
    lyricsTextareaContent,
    isStream,
    updateAiPolishPromptContent,
    stopGenerateLyrics,
    updateAiIdeasAreaShow,
    generateLyricsSmart,
    aiIdeasAreaShow,
    updateStates,
    isFirstClickGenerateLyricsSmart,
    generateLyricsSmartAreaCollapsed,
  } = useAiMusicGeneratorStoreFromZustand()

  const setFormValues = useMusicFormStore((s) => s.setFormValues)
  const { initGeneratorType, onChangeGeneratorType } = useInitGeneratorType()

  const urlPathname = usePathname()
  // home页不展示折叠
  // isEntryPage 是用来判断是否是入口页，比如首页、review页
  const isHomePage = useMemo(() => {
    return urlPathname === '/homepage' || isEntryPage
  }, [urlPathname, isEntryPage])

  // 记录上传图片的状态
  const [imageDisplayStatus, setImageDisplayStatus] =
    useState<ImageDisplayStatus>(ImageDisplayStatus.EMPTY)

  // UploadImage 组件的 ref，用于中断上传
  const uploadImageRef = useRef<UploadImageRef>(null)

  const { mutateAsync: generatePolishPrompt } = useGeneratePolishPromp()
  const { mutate: generateLabelsMutate } =
    api.generation.generateLabels.useMutation({
      onSuccess: (
        data: { title: string; prompt: string; rating: number }[],
      ) => {
        updateStates({
          recommends: data,
        })
      },
    })

  const handleGenerateLyricsFinish = useCallback(
    (generatedLyrics?: string) => {
      // lyricsTextareaContent更新是异步的，可能导致获取不到
      const lyrics = generatedLyrics || lyricsTextareaContent

      // 生成完歌词，静默请求ai优化建议
      generatePolishPrompt({
        prompt: prompt || undefined,
        lyrics,
      }).then((data) => {
        updateAiPolishPromptContent(data)
      })
    },
    [
      generatePolishPrompt,
      prompt,
      lyricsTextareaContent,
      updateAiPolishPromptContent,
    ],
  )

  const startGenerateLyrics = useCallback(() => {
    generateLyricsSmart({
      type: GenerateLyricsSmartType.LYRICS,
      prompt: prompt || '',
      thinking,
      imageUrl,
      generateLyricsFinishCallback: handleGenerateLyricsFinish,
    })
    //同步生成推荐曲风
    generateLabelsMutate({
      prompt: prompt || undefined,
      imageUrl: imageUrl || undefined,
      musicType: 'Song',
    })
  }, [
    generateLyricsSmart,
    prompt,
    thinking,
    imageUrl,
    handleGenerateLyricsFinish,
    generateLabelsMutate,
  ])

  const stopGenerate = useCallback(() => {
    stopGenerateLyrics?.()
  }, [stopGenerateLyrics])
  const { isSignIn } = useSessionInfo()
  const { isMounted } = useIsMounted()
  const { hasSignedInBefore } = useJoinCreditsHintVisibility(isSignIn)

  const showJoinCreditsHint = useMemo(() => {
    return isMounted && !isSignIn && !hasSignedInBefore
  }, [isMounted, isSignIn, hasSignedInBefore])

  const { signUp } = useAppUIModal()
  const guardGenerate = useFeatureGuard(EFeatureName.Generate)

  // 生成歌词的条件
  // 有prompt 或者 有图片
  const isGenerateLyricsCondition = useMemo(() => {
    return (
      (prompt?.trim() || imageUrl) &&
      imageDisplayStatus !== ImageDisplayStatus.LOADING
    )
  }, [prompt, imageUrl, imageDisplayStatus])

  const handleClickGenerate = useCallback(() => {
    if (!isGenerateLyricsCondition) return

    // 未登录
    if (!isSignIn) {
      signUp.show({ defaultShowSignUp: true })
      return
    }

    // 歌词长度超过最大长度
    if (prompt && prompt.length > AI_MUSIC_GENERATOR_MAX_PROMPT_LENGTH) {
      message.info(
        `prompt length exceeds the maximum length, please enter less than ${AI_MUSIC_GENERATOR_MAX_PROMPT_LENGTH} characters`,
      )
      return
    }

    // 有拓展事件，直接执行，相当于平替事件了
    if (typeof outwardEvent === 'function') {
      outwardEvent()
      return
    }

    // 隐藏ai ideas区域
    if (aiIdeasAreaShow) updateAiIdeasAreaShow(false)

    updateStates({
      finalTaskType: taskType,
      finalImageUrl: imageUrl || '',
      finalPromptSelectMode: promptSelectMode as PromptMode,
      finalPrompt: prompt || '',
      aiThinkingAreaShow: false,
    })

    //初始化第二步表单
    form.setFieldsValue({
      description: undefined,
      recommendDescription: undefined,
    })

    onChangeGeneratorType('manual')

    // 快速模式下点击直接提交表单
    if (promptSelectMode === 'quick') {
      form.submit()
      updateStates({
        aiLyriceAreaShow: false,
        styleShow: false,
        aiCreateBtnShow: false,
      })
      return
    }

    // 纯音乐模式下展示style部分
    // 未开启thinking模式
    if (taskType === 'Instrumental') {
      updateStates({
        aiLyriceAreaShow: false,
        styleShow: true,
        reloadRecommend: true,
        isFirstClickGenerateLyricsSmart: false,
        generateLyricsSmartAreaCollapsed: true,
      })
      return
    }

    updateStates({
      aiLyriceAreaShow: false,
      styleShow: false,
      aiCreateBtnShow: false,
    })

    if (isStream) {
      stopGenerate()
    } else {
      updateStates({
        recommends: [],
        isFirstClickGenerateLyricsSmart: false,
        generateLyricsSmartAreaCollapsed: true,
      })
      startGenerateLyrics()
    }
  }, [
    isGenerateLyricsCondition,
    isSignIn,
    signUp,
    prompt,
    promptSelectMode,
    form,
    aiIdeasAreaShow,
    updateAiIdeasAreaShow,
    taskType,
    updateStates,
    isStream,
    stopGenerate,
    startGenerateLyrics,
    imageUrl,
    outwardEvent,
    onChangeGeneratorType,
  ])

  const handleClickGenerateWithGuard = useMemo(
    () => guardGenerate(handleClickGenerate),
    [guardGenerate, handleClickGenerate],
  )

  // 创建节流版本的处理函数
  const throttledHandleClickGenerate = useMemo(
    () => throttle(handleClickGenerateWithGuard, 1000),
    [handleClickGenerateWithGuard],
  )

  useEffect(() => {
    if (isHomePage && isStream) {
      stopGenerate()
    }
  }, [isHomePage, isStream, stopGenerate])

  // 记录是否已经执行过自动生成
  const hasAutoGenerated = useRef(false)

  // 进页面如果是自动生成的，则自动触发函数
  // 从home页进来
  useEffect(() => {
    const hasAllFields = (prompt || imageUrl) && taskType && promptSelectMode
    if (
      initGeneratorType === 'auto' &&
      hasAllFields &&
      !hasAutoGenerated.current
    ) {
      handleClickGenerateWithGuard()
      hasAutoGenerated.current = true
    }
  }, [
    prompt,
    taskType,
    promptSelectMode,
    initGeneratorType,
    handleClickGenerateWithGuard,
    onChangeGeneratorType,
    imageUrl,
  ])

  // 组件卸载时清理节流函数
  useEffect(() => {
    return () => {
      throttledHandleClickGenerate.cancel()
    }
  }, [throttledHandleClickGenerate])

  return (
    <div className={cls`relative mb-5`}>
      {/* 透明蒙层 */}
      {/** 这个蒙层是用来防止用户点击，做禁用状态的，统一处理，就不用每个组件都写 */}
      {isStream && (
        <div className='absolute inset-0 z-10 rounded-lg bg-transparent'></div>
      )}
      {showJoinCreditsHint && (
        <div className='flex justify-end pb-2'>
          <button
            type='button'
            className='before:i-cus--tad-member-gold after:i-com--right-arrow inline-flex size-fit items-center gap-1 rounded-[20px] bg-[#ffcc0029] px-3 py-1.5 text-center text-[rgb(255,235,184)] before:inline-block before:size-4 after:inline-block after:size-4 hover:bg-[#ffcc004d]'
            onClick={() => {
              signUp.show({ defaultShowSignUp: true })
            }}
          >
            {t`Join & Get 25 Free Credits`}
          </button>
        </div>
      )}
      <Form.Item
        name='prompt'
        className={cls`mb-2`}
        noStyle
        rules={[
          {
            transform(value) {
              return value?.trim()
            },
          },
        ]}
      >
        <EnhanceTextArea
          className='music-generator-textarea border-0 bg-transparent px-4'
          imageDisplayStatus={imageDisplayStatus}
          disabled={isStream}
          showMainContent={isHomePage || !generateLyricsSmartAreaCollapsed}
          imageUrl={imageUrl}
          showImage={!!imageUrl}
          style={textareaStyle}
          onKeyDown={(e) => {
            // 支持 Ctrl+Enter 或 Command+Enter 换行
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              // 插入换行符
              const textarea = e.currentTarget as HTMLTextAreaElement
              const start = textarea.selectionStart
              const end = textarea.selectionEnd
              const value = textarea.value

              // 在光标位置插入换行符
              textarea.value = `${value.substring(0, start)}\n${value.substring(end)}`

              // 移动光标到新位置
              textarea.selectionStart = textarea.selectionEnd = start + 1

              // 阻止默认行为
              e.preventDefault()
              return
            }

            // 按回车键且有内容时触发生成
            if (e.key === 'Enter' && !e.shiftKey && isGenerateLyricsCondition) {
              e.preventDefault()
              // 让输入框失焦
              if (e.currentTarget) {
                e.currentTarget.blur()
              }
              throttledHandleClickGenerate()
            }
          }}
          onImageRemove={() => {
            form.setFieldValue('imageUrl', '')
            // 更新imageUrl的缓存
            setFormValues({
              imageUrl: '',
            })
            setImageDisplayStatus(ImageDisplayStatus.EMPTY)

            // 中断图片上传
            uploadImageRef.current?.abortUpload()
          }}
          containerClassName='music-generator-input-shell'
          innerLeftChildren={
            <LeftImageUpload
              form={form}
              imageUrl={imageUrl}
              imageDisplayStatus={imageDisplayStatus}
              setImageDisplayStatus={setImageDisplayStatus}
              uploadImageRef={uploadImageRef}
              isStream={isStream}
              onChangeFormValues={setFormValues}
            />
          }
          resizeIconClassName='translate-x-[-8px]'
          placeholder={t`Tell us about your song`}
          size='large'
          topChildren={
            !isHomePage && !isFirstClickGenerateLyricsSmart ? (
              <TopContent
                isCollapsed={!isHomePage && generateLyricsSmartAreaCollapsed}
                prompt={prompt}
                onClick={() =>
                  updateStates({
                    generateLyricsSmartAreaCollapsed:
                      !generateLyricsSmartAreaCollapsed,
                  })
                }
              />
            ) : null
          }
          bottomChildren={
            <GeneratorBtnActionArea
              form={form}
              throttledHandleClickGenerate={() => {
                throttledHandleClickGenerate()
              }}
              imageDisplayStatus={imageDisplayStatus}
            />
          }
          count={{
            show: false,
            max: AI_MUSIC_GENERATOR_MAX_PROMPT_LENGTH,
            strategy: undefined,
          }}
        />
      </Form.Item>
    </div>
  )
}

export default GeneratorLyricsSmart
